/**
 * ContactDetailsStep
 * Step 3: Contact & Property Details
 */

import { useRef } from "react";
import { FiUser, FiMail, FiPhone, FiMapPin } from "react-icons/fi";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Textarea from "@/components/ui/Textarea";
import { PROPERTY_TYPES } from "@/config/constants";
import { BookingFormData } from "@/types";
import { AddressSuggestion, BookingFormErrors, BookingFormTouched } from "../types";

interface ContactDetailsStepProps {
  formData: Partial<BookingFormData>;
  errors: BookingFormErrors;
  touched: BookingFormTouched;
  // Address autocomplete
  addressQuery: string;
  addressSuggestions: AddressSuggestion[];
  showAddressSuggestions: boolean;
  addressLoading: boolean;
  locatingAddress: boolean;
  suggestionClickedRef: React.MutableRefObject<boolean>;
  // Handlers
  onFormChange: (updates: Partial<BookingFormData>) => void;
  onFieldBlur: (fieldName: string, value: string) => void;
  onPhoneChange: (value: string) => void;
  onAddressQueryChange: (query: string) => void;
  onAddressSelect: (suggestion: AddressSuggestion) => void;
  onAddressFocus: () => void;
  onAddressBlur: () => void;
  onUseMyLocation: () => void;
  onSubmit: () => void;
  onBack: () => void;
  isSubmitting: boolean;
}

export default function ContactDetailsStep({
  formData,
  errors,
  touched,
  addressQuery,
  addressSuggestions,
  showAddressSuggestions,
  addressLoading,
  locatingAddress,
  suggestionClickedRef,
  onFormChange,
  onFieldBlur,
  onPhoneChange,
  onAddressQueryChange,
  onAddressSelect,
  onAddressFocus,
  onAddressBlur,
  onUseMyLocation,
  onSubmit,
  onBack,
  isSubmitting,
}: ContactDetailsStepProps) {
  const addressInputRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-6">
      {/* Validation Error Summary */}
      {Object.keys(errors).length > 0 && touched.customerName && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                Please complete all required fields
              </h3>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                {errors.customerName && <li>• {errors.customerName}</li>}
                {errors.customerEmail && <li>• {errors.customerEmail}</li>}
                {errors.customerPhone && <li>• {errors.customerPhone}</li>}
                {errors.propertyAddress && <li>• {errors.propertyAddress}</li>}
                {errors.propertyType && <li>• {errors.propertyType}</li>}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <h2 className="text-2xl font-bold text-neutral-900 dark:text-white flex items-center gap-2">
        <FiUser className="text-primary-600" />
        Contact & Property Details
      </h2>

      {/* Contact Information */}
      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Full Name *"
          leftIcon={<FiUser />}
          value={formData.customerName || ""}
          onChange={(e) => onFormChange({ customerName: e.target.value })}
          onBlur={(e) => onFieldBlur("customerName", e.target.value)}
          error={touched.customerName ? errors.customerName : undefined}
          placeholder="John Doe"
          aria-required="true"
          aria-invalid={!!errors.customerName}
        />

        <Input
          label="Email *"
          type="email"
          leftIcon={<FiMail />}
          value={formData.customerEmail || ""}
          onChange={(e) => onFormChange({ customerEmail: e.target.value })}
          onBlur={(e) => onFieldBlur("customerEmail", e.target.value)}
          error={touched.customerEmail ? errors.customerEmail : undefined}
          placeholder="john@example.com"
          aria-required="true"
          aria-invalid={!!errors.customerEmail}
        />

        <Input
          label="Phone Number *"
          type="tel"
          leftIcon={<FiPhone />}
          value={formData.customerPhone || ""}
          onChange={(e) => onPhoneChange(e.target.value)}
          onBlur={(e) => onFieldBlur("customerPhone", e.target.value)}
          error={touched.customerPhone ? errors.customerPhone : undefined}
          placeholder="(555) 123-4567"
          maxLength={14}
          aria-required="true"
          aria-invalid={!!errors.customerPhone}
        />
      </div>

      {/* Property Address */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Property Address *
          </label>
          <button
            type="button"
            onClick={onUseMyLocation}
            disabled={locatingAddress}
            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium flex items-center gap-1 disabled:opacity-50"
          >
            <FiMapPin className="w-3 h-3" />
            {locatingAddress ? "Locating..." : "Use My Location"}
          </button>
        </div>
        <div className="relative">
          <Input
            ref={addressInputRef}
            leftIcon={<FiMapPin />}
            value={addressQuery}
            disabled={locatingAddress}
            onChange={(e) => {
              const value = e.target.value;
              onAddressQueryChange(value);
              onFormChange({ propertyAddress: value });
            }}
            onFocus={onAddressFocus}
            onBlur={(e) => {
              setTimeout(() => {
                if (!suggestionClickedRef.current) {
                  onAddressBlur();
                }
              }, 200);
              onFieldBlur("propertyAddress", e.target.value);
            }}
            error={touched.propertyAddress ? errors.propertyAddress : undefined}
            placeholder="123 Main St, City, State 12345"
            aria-required="true"
            aria-invalid={!!errors.propertyAddress}
          />

          {/* Address Suggestions Dropdown */}
          {showAddressSuggestions && (addressLoading || addressSuggestions.length > 0) && (
            <div className="absolute z-10 w-full mt-1 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {addressLoading && (
                <div className="p-3 text-sm text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  Searching…
                </div>
              )}
              {addressSuggestions.map((suggestion) => (
                <div
                  key={suggestion.placeId}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onAddressSelect(suggestion)}
                  className="p-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 cursor-pointer border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
                >
                  <div className="font-medium text-neutral-900 dark:text-white">
                    {suggestion.mainText}
                  </div>
                  {suggestion.secondaryText && (
                    <div className="text-sm text-neutral-600 dark:text-neutral-300">
                      {suggestion.secondaryText}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Loading Overlay */}
          {locatingAddress && (
            <div className="absolute inset-0 z-20 rounded-lg bg-white/60 dark:bg-neutral-800/60 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          )}
        </div>
      </div>

      {/* Property Type */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
          Property Type *
        </label>
        <select
          name="propertyType"
          className={`w-full px-4 py-2 border rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-neutral-900 dark:text-white ${
            errors.propertyType ? "border-red-500" : "border-neutral-300 dark:border-neutral-700"
          }`}
          value={formData.propertyType || ""}
          onChange={(e) => onFormChange({ propertyType: e.target.value })}
          onBlur={(e) => onFieldBlur("propertyType", e.target.value)}
          aria-required="true"
          aria-invalid={!!errors.propertyType}
        >
          <option value="">Select property type</option>
          {PROPERTY_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        {errors.propertyType && <p className="mt-1 text-sm text-red-600">{errors.propertyType}</p>}
      </div>

      {/* MLS Number (Optional) */}
      <Input
        label="MLS Number (Optional)"
        value={formData.mlsNumber || ""}
        onChange={(e) => onFormChange({ mlsNumber: e.target.value })}
        placeholder="MLS12345678"
      />

      {/* Special Instructions */}
      <Textarea
        label="Special Instructions (Optional)"
        value={formData.specialInstructions || ""}
        onChange={(e) => onFormChange({ specialInstructions: e.target.value })}
        placeholder="Any specific requirements, access codes, or notes for the photographer..."
        rows={3}
      />

      {/* Navigation */}
      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={onSubmit} loading={isSubmitting}>
          Review & Add to Cart
        </Button>
      </div>
    </div>
  );
}
